/**
 * One month of the trainee's log, summarised — what the calendar grid draws.
 *
 * Separate from `useFoodLogV2` because it answers a different question off a different endpoint: that
 * hook fetches the WEEK holding the open day, with every meal and food in it, and this one fetches a
 * month of per-day totals with no meals at all. The screen runs both, and their loading and error
 * states are kept apart on purpose — a month that fails to load must not blank the day a trainer is
 * reading, and a failed week must not blank the calendar.
 */

import { useEffect, useState } from "react";
import moment from "moment";
import { getMonthSummary, type DaySummaryV2 } from "../../../../services/FoodLogService";
import { KEY } from "./hebrewDates";

const EMPTY: DaySummaryV2[] = [];

export const useMonthLogV2 = (
  traineeId: string,
  /** YYYY-MM-01 */
  monthStart: string
): { days: DaySummaryV2[]; loading: boolean; error: unknown } => {
  const [days, setDays] = useState<DaySummaryV2[]>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!traineeId || !monthStart) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const to = moment(monthStart, KEY).clone().endOf("month").format(KEY);

    getMonthSummary(traineeId, monthStart, to)
      .then((result) => {
        if (!cancelled) setDays(result);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        // Cleared, for the same reason the week hook clears: a failed fetch that left the PREVIOUS
        // month on screen would paint one month's adherence under another month's dates — and a
        // calendar makes that unusually convincing.
        setDays(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [traineeId, monthStart]);

  return { days, loading, error };
};
